/* eslint-disable @typescript-eslint/no-unused-vars */
import React, { useState, useEffect, useCallback } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { MoreVertIcon } from "../../components/icons";
import {
  SettingsIcon,
  ChevronLeftIcon,
  ChevronRightIcon,
  ArchiveIcon,
  ShareIcon,
} from "../../components/icons";
import ProjectTable from "../../components/ProjectTable/ProjectTable";
import "./MfaAppPage.scss";
import {
  PulseButton,
  PulseButtonTheme,
  PulsePopupContainerSize,
} from "@pulselabs-ai/shared-fe-ui";
import CustomDialog from "../../components/customDialog/CustomDialog";
import { FlightRecorderProjectDTO } from "../../types/flight-recorder.types";
import MfaAppService from "../../services/MfaAppService";
import { IconButton, Menu, MenuItem, Divider } from "@mui/material";
import FeedbackCard from "../../components/FeedbackCard/FeedbackCard";
import DetailPanel from "../../components/DetailPanel/DetailPanel";
import { TableRow } from "../../components/ProjectTable/ProjectTable";
import MfaCaptureMethodService, {
  CaptureMethod,
} from "../../services/MfaCaptureMethodService";
import { useAppSelector } from "../../store/hooks";
import MfaSDKResponseService, {
  SDKResponse,
} from "../../services/MfaSDKResponseService";
import { useDispatch } from "react-redux";
import {
  setSelectedProject,
  clearSelectedProject,
} from "../../store/slices/onboardingSlice";
import SDKDocsDialog from "../../components/SDKDocsDialog/SDKDocsDialog";
import Loader from "../../components/PulseLoader/loader";
import { findQuestionDetails, getAnswerText } from "../../utils";
import Breadcrumbs, { BreadcrumbItem } from "../../components/Breadcrumbs";
import MfaShareAppModal from "../../components/dialogs/MfaShareAppModal/MfaShareAppModal";
import { useArchiveAppConfirmation } from "../../hooks/useArchiveAppConfirmation";
import ArchiveAppDialog from "../../components/ArchiveAppDialog/ArchiveAppDialog";

const MfaAppPage: React.FC = () => {
  const { projectId } = useParams<{ projectId: string }>();
  const navigate = useNavigate();
  const [isShareModalOpen, setIsShareModalOpen] = useState(false);
  const [project, setProject] = useState<FlightRecorderProjectDTO | null>(null);
  const [selectedCard, setSelectedCard] = useState<number | null>(null);
  const [isPanelOpen, setIsPanelOpen] = useState(false);
  const [selectedRow, setSelectedRow] = useState<TableRow | null>(null);
  const [error, setError] = useState<string | null>(null);

  // New state for captures
  const userData = useAppSelector((state) => state.user.user);
  const token = userData?.token;
  const workspaceId = userData?.defaultWorkspace?.id?.toString() || "";
  const [captures, setCaptures] = useState<CaptureMethod[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const userId = userData?.id;

  const [tableData, setTableData] = useState<TableRow[]>([]);
  const [feedbackResponse, setFeedbackResponse] = useState<SDKResponse | null>(
    null,
  );

  const [currentIndex, setCurrentIndex] = useState(0);
  const cardsPerPage = 3;

  const dispatch = useDispatch();

  // Add state for dialog
  const [isDocsDialogOpen, setIsDocsDialogOpen] = useState(false);

  const [responseCounts, setResponseCounts] = useState<{
    [key: string]: number;
  }>({});
  const [loadingCounts, setLoadingCounts] = useState(true);
  const [isInitialLoad, setIsInitialLoad] = useState(true);

  // Add state for delayed loading
  const [showContent, setShowContent] = useState(false);

  const [currentCapture, setCurrentCapture] = useState<CaptureMethod | null>(
    null,
  );

  const [menuAnchorEl, setMenuAnchorEl] = useState<null | HTMLElement>(null);
  const menuOpen = Boolean(menuAnchorEl);

  // Add state for the MfaShareAppModal modal
  const [isMfaShareModalOpen, setIsMfaShareModalOpen] = useState(false);

  // Added breadcrumbItems definition
  const breadcrumbItems: BreadcrumbItem[] = [
    { label: "Apps", path: "/" },
    {
      label: project?.project.projectName || "App",
      path: `/project/${projectId}`,
    },
  ];

  const handlePrevious = () => {
    setCurrentIndex(Math.max(0, currentIndex - 1));
  };

  const handleNext = () => {
    setCurrentIndex(
      Math.max(0, Math.min(captures.length - cardsPerPage, currentIndex + 1)),
    );
  };

  useEffect(() => {
    const fetchProject = async () => {
      if (!projectId || !userId) return;
      try {
        const mfaAppService = MfaAppService.getInstance();
        const fetchedProject = await mfaAppService.getProject(
          projectId,
          userId.toString(),
        );
        console.log("Fetched project:", fetchedProject);
        setProject(fetchedProject);
      } catch (err) {
        console.error("Failed to fetch project:", err);
      }
    };
    fetchProject();
  }, [projectId, userId]);

  // New effect for fetching captures
  useEffect(() => {
    if (projectId && token) {
      handleGetCaptures();
    }
  }, [projectId, token]);

  useEffect(() => {
    console.log("Loading state:", isLoading);
  }, [isLoading]);

  const handleGetCaptures = async () => {
    try {
      if (!projectId) return;
      console.log("Starting to fetch captures...");
      setIsLoading(true);

      const mfaCaptureMethodService = MfaCaptureMethodService.getInstance();
      const response =
        await mfaCaptureMethodService.getCapturesByProjectId(projectId);
      console.log("Captures fetched successfully:", response.data);
      setCaptures(response.data);
    } catch (error) {
      console.error("Failed to get captures:", error);
      setError(
        error instanceof Error ? error.message : "Failed to fetch captures",
      );
    } finally {
      console.log("Finished fetching captures");
      setIsLoading(false);
    }
  };

  const handleRowClick = (row: TableRow) => {
    console.log("Row clicked:", row);
    setSelectedRow(row);
    setIsPanelOpen(true);
  };

  const handleCardSelect = async (captureId: number) => {
    // Check if card is already selected
    if (selectedCard === captureId) {
      console.log("Card already selected, skipping API calls");
      return;
    }

    setIsLoading(true);
    setSelectedCard(captureId);
    setTableData([]);

    try {
      if (!token || !userId) return;

      // Get the capture method to access the survey schema
      const captureService = MfaCaptureMethodService.getInstance();
      const capture = await captureService.getCaptureMethod(captureId);
      setCurrentCapture(capture);

      const mfaSDKResponseService = MfaSDKResponseService.getInstance();
      const response = await mfaSDKResponseService.getCaptureResponses(
        captureId.toString(),
        userId.toString(),
        token,
        workspaceId,
      );

      // Guard against empty response
      if (!response || !Array.isArray(response) || response.length === 0) {
        console.error("Invalid response structure:", response);
        setTableData([]); // Clear table on invalid response
        return;
      }

      setFeedbackResponse(response[0]); // Keep first response for detail panel

      // Map responses to table rows
      const tableRows = response.map((feedbackResponse) => ({
        surveyResponseId: feedbackResponse.surveyResponseId || "N/A",
        createdAt: feedbackResponse.createdAt
          ? new Date(feedbackResponse.createdAt).toLocaleString()
          : "N/A",
        videoUrl: feedbackResponse.videoUrl || "",
        thumbnailUrl: feedbackResponse.thumbnailUrl || "",
        userId: feedbackResponse.userId || "N/A",
        userMetadata: {
          deviceType: feedbackResponse.userMetadata?.deviceType || "Unknown",
          osType: feedbackResponse.userMetadata?.osType || "Unknown",
          version: feedbackResponse.userMetadata?.version || "Unknown",
        },
        surveyResponse: {
          title: "",
          description: "",
          questions: [],
          ...(typeof feedbackResponse.surveyResponse === "object"
            ? feedbackResponse.surveyResponse
            : {}),
        },
      }));

      setTableData(tableRows);
      setSelectedRow(tableRows[0]);
    } catch (error: unknown) {
      console.error("Failed to fetch capture responses:", error);
      setError(
        error instanceof Error ? error.message : "An unknown error occurred",
      );
      setTableData([]); // Clear table on error
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    if (error) {
      console.log("Error state updated:", error);
    }
  }, [error]);

  // Add effect to handle initial capture load
  useEffect(() => {
    if (captures.length > 0 && !selectedCard) {
      // Automatically select and load data for first capture
      handleCardSelect(captures[0].captureId);
    }
  }, [captures]); // Only run when captures array changes

  useEffect(() => {
    if (projectId) {
      dispatch(setSelectedProject(projectId));
    }

    return () => {
      dispatch(clearSelectedProject());
    };
  }, [projectId, dispatch]);

  useEffect(() => {
    const fetchResponseCounts = async () => {
      if (!projectId || !token) return;

      try {
        const sdkService = MfaSDKResponseService.getInstance();
        const counts = await sdkService.getResponseCounts(
          projectId,
          workspaceId,
          token,
        );
        if (Array.isArray(counts)) {
          const countsObject = Object.fromEntries(
            counts.map((count) => [count.captureId, count.count]),
          );
          setResponseCounts(countsObject);
        } else if (typeof counts === "object" && counts !== null) {
          setResponseCounts(counts);
        } else {
          console.error("Invalid response counts format:", counts);
          setResponseCounts({});
        }
      } catch (error) {
        console.error("Failed to fetch response counts:", error);
        setResponseCounts({});
      } finally {
        setLoadingCounts(false);
        setIsInitialLoad(false);
      }
    };

    fetchResponseCounts();
  }, [projectId, token]);

  const displayRowCount = (captureId: string) => {
    if (isInitialLoad || loadingCounts) return ""; // Show blank while loading
    return responseCounts[captureId] ?? 0;
  };

  console.log("responses", feedbackResponse?.surveyResponse);

  // Update DetailPanel data mapping if you're using it
  const detailPanelData =
    selectedRow && currentCapture?.surveySchema
      ? {
          createdAt: selectedRow.createdAt,
          videoUrl: selectedRow.videoUrl,
          feedback: {
            responses: Object.entries(selectedRow.surveyResponse)
              .map(([key, value]) => {
                const questionDetails = findQuestionDetails(
                  currentCapture.surveySchema,
                  key,
                );
                if (!questionDetails) return null;

                return {
                  question: questionDetails.title,
                  response: questionDetails.choices
                    ? getAnswerText(questionDetails, String(value))
                    : String(value),
                  displayName: key,
                };
              })
              .filter(Boolean) as {
              question: string;
              response: string;
              displayName: string;
            }[],
          },
          device: {
            type: selectedRow.userMetadata.deviceType,
            osType: selectedRow.userMetadata.osType,
            osVersion: selectedRow.userMetadata.version,
            userId: selectedRow.userId,
          },
        }
      : undefined;

  // Add useEffect to handle delayed content display
  useEffect(() => {
    if (!isLoading) {
      const timer = setTimeout(() => {
        setShowContent(true);
      }, 250);
      return () => clearTimeout(timer);
    }
  }, [isLoading]);

  const handleMenuClick = (event: React.MouseEvent<HTMLElement>) => {
    setMenuAnchorEl(event.currentTarget);
  };

  const handleMenuClose = () => {
    setMenuAnchorEl(null);
  };

  // Updated handleShareApp to open the MfaShareAppModal modal
  const handleShareApp = () => {
    setIsMfaShareModalOpen(true); // Open the new modal
    handleMenuClose(); // Close the menu
  };

  // Handler to close the MfaShareAppModal modal
  const handleCloseMfaShareModal = () => {
    setIsMfaShareModalOpen(false);
  };

  // Add state and handlers from the hook
  const {
    isArchiveConfirmOpen,
    itemToArchive,
    isArchiving,
    archiveError,
    openArchiveConfirm,
    closeArchiveConfirm,
    confirmArchiveAction,
  } = useArchiveAppConfirmation({
    // After archiving on this page, likely navigate back to the apps list
    onSuccess: () => navigate("/"),
  });

  // Updated handleArchiveApp to use the hook
  const handleArchiveApp = () => {
    if (project) {
      openArchiveConfirm({
        id: project.project.id,
        name: project.project.projectName,
      });
    }
    handleMenuClose(); // Close the menu regardless
  };

  return (
    <div className="app-page page-container">
      <div className="project-header">
        <div className="header-content">
          <div className="left-section">
            <Breadcrumbs items={breadcrumbItems} />
          </div>
          <div className="right-section">
            {/* <PulseButton
              isonlyicon={true}
              theme={PulseButtonTheme.LINK_BLACK}
              onClick={() => setIsShareModalOpen(true)}
              className="share-button"
            >
              <ShareIcon />
            </PulseButton> */}
            <PulseButton
              // isonlyicon={true}
              theme={PulseButtonTheme.PRIMARY}
              onClick={() => navigate(`/project/${projectId}/settings`)}
              className="settings-button"
              startIcon={<SettingsIcon />}
            >
              Settings
            </PulseButton>
            {project?.project.status !== "INACTIVE" && (
              <PulseButton
                isonlyicon={true}
                theme={PulseButtonTheme.LINK_BLACK}
                onClick={handleMenuClick}
                className="menu-button"
                startIcon={<MoreVertIcon />}
              ></PulseButton>
            )}
          </div>
        </div>
      </div>

      <div className="feedback-cards-row">
        <IconButton
          className={`carousel-arrow left ${currentIndex === 0 ? "disabled" : ""}`}
          onClick={handlePrevious}
          disabled={currentIndex === 0}
        >
          <ChevronLeftIcon />
        </IconButton>

        <div
          className="carousel-container"
          style={{
            transform: `translateX(-${currentIndex * (100 / cardsPerPage)}%)`,
          }}
        >
          {captures.map((capture) => {
            let title = "Untitled Capture";
            let questionsCount = 0;

            try {
              if (capture.surveySchema) {
                let surveyData;

                try {
                  // Check if it's already an object
                  surveyData =
                    typeof capture.surveySchema === "string"
                      ? JSON.parse(capture.surveySchema)
                      : capture.surveySchema;
                } catch {
                  surveyData = { title: "parse error" };
                  console.error(
                    "Failed to parse survey schema:",
                    capture.surveySchema,
                  );
                }
                // const surveyData = capture.surveySchema;
                title = surveyData.title || "Untitled Capture";

                questionsCount = surveyData.pages?.[0]?.elements?.length || 0;
              }
            } catch (e) {
              console.error("Error parsing surveySchema:", e);
            }

            return (
              <FeedbackCard
                key={capture.captureId}
                title={title}
                status={capture.status}
                triggerType={capture.actionType}
                questionsCount={questionsCount}
                onViewCode={() =>
                  console.log("View code clicked", capture.captureId)
                }
                onEdit={() => console.log("Edit clicked", capture.captureId)}
                onDeactivate={() =>
                  console.log("Deactivate clicked", capture.captureId)
                }
                rowCount={displayRowCount(capture.captureId.toString())}
                selectable={true}
                selected={selectedCard === capture.captureId}
                onSelect={() => handleCardSelect(capture.captureId)}
                menuVisible={false}
                style={{ flex: `0 0 ${100 / cardsPerPage}%` }}
              />
            );
          })}
        </div>

        <IconButton
          className={`carousel-arrow right ${currentIndex >= captures.length - cardsPerPage ? "disabled" : ""}`}
          onClick={handleNext}
          disabled={currentIndex >= captures.length - cardsPerPage}
        >
          <ChevronRightIcon />
        </IconButton>
      </div>

      <div className="project-content">
        {isLoading || !showContent || loadingCounts ? (
          <Loader />
        ) : tableData.length > 0 ? (
          <ProjectTable
            onRowClick={handleRowClick}
            data={tableData}
            surveySchema={currentCapture?.surveySchema}
          />
        ) : captures.length > 0 ? (
          <div className="no-data-message">
            <div>
              It appears that you haven&apos;t received any responses yet.
            </div>
            <div>
              Check your development configuration to make sure that everything
              is set up correctly.
            </div>
            {/* <PulseButton onClick={() => setIsDocsDialogOpen(true)}>
              REVIEW CONFIGURATION
            </PulseButton> */}
          </div>
        ) : null}
      </div>

      <CustomDialog
        isOpen={isShareModalOpen}
        onClose={() => setIsShareModalOpen(false)}
        containerSize={PulsePopupContainerSize.SMALL}
        showCloseIcon={true}
        isBackgroundClose={false}
        roundedCorners={true}
      >
        <div className="share-modal">
          <h2>Share Project</h2>
        </div>
      </CustomDialog>

      <DetailPanel
        isOpen={isPanelOpen}
        onClose={() => setIsPanelOpen(false)}
        data={detailPanelData}
      />

      <SDKDocsDialog
        isOpen={isDocsDialogOpen}
        onClose={() => setIsDocsDialogOpen(false)}
      />

      <Menu
        anchorEl={menuAnchorEl}
        open={menuOpen}
        onClose={handleMenuClose}
        anchorOrigin={{
          vertical: "bottom",
          horizontal: "right",
        }}
        transformOrigin={{
          vertical: "top",
          horizontal: "right",
        }}
        PaperProps={{
          style: {
            width: 342,
          },
        }}
      >
        <MenuItem onClick={handleShareApp} style={{ padding: "8px 16px" }}>
          <div style={{ display: "flex", alignItems: "center", gap: "8px" }}>
            <ShareIcon />
            <span>Share App</span>
          </div>
        </MenuItem>
        <Divider style={{ margin: "4px 0" }} />
        <MenuItem onClick={handleArchiveApp} style={{ padding: "8px 16px" }}>
          <div style={{ display: "flex", alignItems: "center", gap: "8px" }}>
            <ArchiveIcon />
            <span>Archive App</span>
          </div>
        </MenuItem>
      </Menu>

      {/* Render the MfaShareAppModal modal */}
      <MfaShareAppModal
        isOpen={isMfaShareModalOpen}
        onClose={handleCloseMfaShareModal}
        projectId={projectId || null} // Pass the current projectId
      />

      {/* Use the new ArchiveAppDialog */}
      <ArchiveAppDialog
        isOpen={isArchiveConfirmOpen}
        onClose={closeArchiveConfirm}
        onConfirm={confirmArchiveAction}
        appName={itemToArchive?.name} // Pass app name to dialog
        isArchiving={isArchiving} // Pass loading state
        // error={archiveError} // Optionally display error
      />
    </div>
  );
};

export default MfaAppPage;
